import { useState } from 'react';
import {
  Box,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Slider,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import {
  Delete,
  MoreVert,
  FlipToFront,
  FlipToBack,
  Settings,
} from '@mui/icons-material';
import type { EllipseElement as EllipseElementType } from '@/types/thinkspace';
import { ColorPalette } from '@/components/common/ColorPalette';

type EllipseElementProps = {
  element: EllipseElementType;
  selected: boolean;
  canEdit: boolean;
  onSelect: () => void;
  onUpdate?: (updates: Partial<EllipseElementType>) => void;
  onDelete: () => void;
  onBringToFront: () => void;
  onSendToBack: () => void;
  onBringForward?: () => void;
  onSendBackward?: () => void;
  onDragStart: (e: React.MouseEvent | React.TouchEvent) => void;
};

export const EllipseElement = ({
  element,
  selected,
  canEdit,
  onSelect,
  onUpdate,
  onDelete,
  onBringToFront,
  onSendToBack,
  onDragStart,
}: EllipseElementProps) => {
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [propsOpen, setPropsOpen] = useState(false);
  const [fillColor, setFillColor] = useState(element.fillColor || '');
  const [useTransparentFill, setUseTransparentFill] = useState(!element.fillColor);
  const [strokeColor, setStrokeColor] = useState(element.strokeColor);
  const [strokeWidth, setStrokeWidth] = useState(element.strokeWidth);

  const rx = element.size.width / 2;
  const ry = element.size.height / 2;

  return (
    <Box
      onClick={(e) => {
        e.stopPropagation();
        onSelect();
      }}
      onDoubleClick={(e) => {
        e.stopPropagation();
        if (canEdit) {
          setPropsOpen(true);
        }
      }}
      onMouseDown={canEdit && !propsOpen ? onDragStart : undefined}
      onTouchStart={canEdit && !propsOpen ? onDragStart : undefined}
      sx={{
        position: 'absolute',
        left: element.position.x,
        top: element.position.y,
        width: element.size.width,
        height: element.size.height,
        cursor: canEdit ? 'grab' : 'default',
        userSelect: 'none',
        touchAction: 'none',
        zIndex: element.zIndex,
      }}
    >
      <svg
        width={element.size.width}
        height={element.size.height}
        style={{ overflow: 'visible' }}
      >
        <ellipse
          cx={rx}
          cy={ry}
          rx={rx - element.strokeWidth / 2}
          ry={ry - element.strokeWidth / 2}
          fill={element.fillColor || 'transparent'}
          stroke={element.strokeColor}
          strokeWidth={element.strokeWidth}
        />
        {selected && (
          <ellipse
            cx={rx}
            cy={ry}
            rx={rx + 4}
            ry={ry + 4}
            fill="none"
            stroke="#0f2ccf"
            strokeWidth={3}
          />
        )}
      </svg>

      {canEdit && selected && (
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            setMenuAnchor(e.currentTarget);
          }}
          sx={{
            position: 'absolute',
            top: -20,
            right: -20,
            backgroundColor: 'white',
            boxShadow: 1,
            '&:hover': { backgroundColor: 'grey.100' },
          }}
        >
          <MoreVert fontSize="small" />
        </IconButton>
      )}

      {/* Resize handle when selected */}
      {canEdit && selected && (
        <Box
          sx={{
            position: 'absolute',
            right: -6,
            bottom: -6,
            width: 12,
            height: 12,
            backgroundColor: '#0f2ccf',
            borderRadius: 1,
            cursor: 'nwse-resize',
          }}
        />
      )}

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        onClick={(e) => e.stopPropagation()}
      >
        <MenuItem onClick={() => { setMenuAnchor(null); setPropsOpen(true); }}>
          <ListItemIcon><Settings fontSize="small" /></ListItemIcon>
          <ListItemText>Properties</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { setMenuAnchor(null); onBringToFront(); }}>
          <ListItemIcon><FlipToFront fontSize="small" /></ListItemIcon>
          <ListItemText>Bring to Front</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { setMenuAnchor(null); onSendToBack(); }}>
          <ListItemIcon><FlipToBack fontSize="small" /></ListItemIcon>
          <ListItemText>Send to Back</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { setMenuAnchor(null); onDelete(); }} sx={{ color: 'error.main' }}>
          <ListItemIcon><Delete fontSize="small" color="error" /></ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>

      {/* Properties Dialog */}
      <Dialog
        open={propsOpen}
        onClose={() => setPropsOpen(false)}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <DialogTitle>Ellipse Properties</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, minWidth: 300 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={useTransparentFill}
                  onChange={(e) => setUseTransparentFill(e.target.checked)}
                />
              }
              label="Transparent Fill"
              sx={{ mb: 2 }}
            />

            {!useTransparentFill && (
              <>
                <Typography gutterBottom>Fill Color</Typography>
                <Box sx={{ mb: 3 }}>
                  <ColorPalette
                    value={fillColor || '#ffffff'}
                    onChange={setFillColor}
                    includeWhite
                  />
                </Box>
              </>
            )}

            <Typography gutterBottom>Border Color</Typography>
            <Box sx={{ mb: 3 }}>
              <ColorPalette
                value={strokeColor}
                onChange={setStrokeColor}
              />
            </Box>

            <Typography gutterBottom>Border Width: {strokeWidth}px</Typography>
            <Slider
              value={strokeWidth}
              onChange={(_, value) => setStrokeWidth(value as number)}
              min={0}
              max={20}
              step={1}
              valueLabelDisplay="auto"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPropsOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => {
              onUpdate?.({
                fillColor: useTransparentFill ? null : fillColor,
                strokeColor,
                strokeWidth,
              });
              setPropsOpen(false);
            }}
          >
            Apply
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
